import { Link } from 'react-router-dom';

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  category: string;
  excerpt: string;
  image: string;
  author: {
    name: string;
    avatar?: string;
    role: string;
  };
  date: string;
  readTime: string;
}

interface BlogCardProps {
  post: BlogPost;
  variant?: 'default' | 'horizontal';
}

export default function BlogCard({ 
  post,
  variant = 'default'
}: BlogCardProps) {
  const { title, slug, category, excerpt, image, author, date, readTime } = post;
  
  if (variant === 'horizontal') {
    return (
      <Link 
        to={`/artigo/${slug}`}
        className="group flex gap-6 pb-6 border-b border-gray-100 last:border-0 hover:bg-gradient-to-r hover:from-gray-50 hover:to-white p-5 rounded-xl transition-all duration-300 cursor-pointer"
      >
        <div className="relative w-40 h-40 flex-shrink-0 rounded-xl overflow-hidden shadow-md">
          <img 
            src={image} 
            alt={title}
            className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        </div>
        
        <div className="flex-1">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-[#1E40AF] to-[#3B82F6] text-white text-xs font-bold rounded-full mb-3 shadow-sm">
            <i className="ri-bookmark-line"></i>
            {category}
          </span>
          <h3 className="text-lg font-bold text-[#0F172A] leading-snug mb-3 line-clamp-2 group-hover:text-[#1E40AF] transition-colors">
            {title}
          </h3>
          <p className="text-sm text-[#64748B] leading-relaxed mb-3 line-clamp-2">
            {excerpt}
          </p>
          <div className="flex items-center gap-4 text-xs text-[#94A3B8]">
            <span className="flex items-center gap-1.5 font-medium">
              <i className="ri-user-line text-sm"></i>
              {author.name}
            </span>
            <span className="text-gray-300">•</span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line text-sm"></i>
              {new Date(date).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })}
            </span>
            <span className="text-gray-300">•</span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line text-sm"></i>
              {readTime}
            </span>
          </div>
        </div>
        
        <div className="flex items-center">
          <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#EFF6FF] text-[#1E40AF] group-hover:bg-[#1E40AF] group-hover:text-white transition-all duration-300 group-hover:scale-110">
            <i className="ri-arrow-right-line text-xl"></i>
          </div>
        </div>
      </Link>
    );
  }
  
  return (
    <Link 
      to={`/artigo/${slug}`}
      className="group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-500 cursor-pointer border border-gray-100 hover:border-[#1E40AF]/20 hover:-translate-y-2"
    >
      <div className="relative w-full h-64 overflow-hidden">
        <img 
          src={image} 
          alt={title}
          className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-[#1E40AF] to-[#3B82F6] text-white text-xs font-bold rounded-full shadow-lg">
          <i className="ri-bookmark-line"></i>
          {category}
        </span>
      </div>
      
      <div className="p-6">
        <h3 className="text-xl font-bold text-[#0F172A] leading-tight mb-3 line-clamp-2 group-hover:text-[#1E40AF] transition-colors min-h-[56px]">
          {title}
        </h3>
        
        <p className="text-[#64748B] text-sm leading-relaxed mb-5 line-clamp-3 min-h-[63px]">
          {excerpt}
        </p>
        
        <div className="flex items-center justify-between pt-5 border-t border-gray-100">
          <div className="flex items-center gap-3">
            {author?.avatar ? (
              <img 
                src={author.avatar} 
                alt={author.name}
                className="w-11 h-11 rounded-full object-cover ring-2 ring-gray-100 group-hover:ring-[#1E40AF]/30 transition-all"
              />
            ) : (
              <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#1E40AF] to-[#3B82F6] flex items-center justify-center text-white font-bold text-sm ring-2 ring-gray-100 group-hover:ring-[#1E40AF]/30 transition-all">
                {author?.name?.charAt(0) || 'A'}
              </div>
            )}
            <div>
              <p className="text-sm font-bold text-[#0F172A]">{author?.name || 'Autor'}</p>
              <p className="text-xs text-[#94A3B8] flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {new Date(date).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })}
              </p>
            </div>
          </div>
          
          <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#EFF6FF] text-[#1E40AF] group-hover:bg-[#1E40AF] group-hover:text-white transition-all duration-300 group-hover:scale-110 group-hover:rotate-[-10deg]">
            <i className="ri-arrow-right-line text-xl"></i>
          </div>
        </div>
      </div>
    </Link>
  );
}